import clsx from "clsx";
import React, { forwardRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import SpinningCircle from "../common/SpinningCircle";

interface PhoneProps {
	value?: string;
	error?: string;
	className?: string;
	id: string;
	label?: string;
	placeholder?: string;
	maxLength?: number;
	areaCode?: string;
	autoComplete?: string;
	autoFocus?: boolean;
	withSpinner?: boolean;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const Phone = forwardRef<HTMLInputElement, PhoneProps>((props, ref) => {
	const {
		value,
		error,
		className,
		id,
		label,
		placeholder,
		maxLength,
		areaCode,
		autoComplete,
		autoFocus,
		withSpinner = false,
		onChange,
		onBlur,
		onEnter,
	} = props;

	const { formatMessage } = useIntl();

	const handleOnKeyDown = (e) => {
		if (e.key === "Enter" && onEnter) {
			onEnter(e);
		}
	};

	const handleOnChange = (event) => {
		const {
			target: { value },
			nativeEvent: { inputType },
		} = event;

		if (
			(/^\d+$/.test(value) || value === "") &&
			inputType !== "insertFromPaste"
		) {
			onChange(event);
		}
	};

	const handleOnPaste = ({ clipboardData }) => {
		handleOnChange({
			target: {
				id: "phone",
				value: clipboardData.getData("text/plain").replaceAll(" ", ""),
			},
			nativeEvent: {
				inputType: "customOnPaste",
			},
		});
	};

	const hasErrors = error || (!error && value && value.length !== +maxLength);

	return (
		<>
			<div
				className={clsx({
					[`${className}`]: true,
					error: hasErrors,
					valid: value && !error,
				})}
			>
				<label className="label" htmlFor={id}>
					{label}
				</label>
				{areaCode && (
					<span data-testid={`${id}.areaCode`} className="areaCode">
						{`+${areaCode}`}
					</span>
				)}
				<input
					ref={ref}
					className="field"
					id={id}
					placeholder={placeholder}
					type="tel"
					maxLength={maxLength}
					aria-invalid={!!hasErrors}
					autoFocus={autoFocus}
					autoComplete={autoComplete}
					data-testid={`${id}.input`}
					data-automationid={id}
					onPaste={handleOnPaste}
					onChange={handleOnChange}
					onBlur={onBlur}
					onKeyDown={handleOnKeyDown}
				/>
				{error && (
					<span
						className="errorMessage"
						data-automationid={`${id}.error`}
						data-testid={`${id}.error`}
					>
						<FormattedMessage id={error} values={{ fieldname: label }} />
					</span>
				)}
			</div>
			{withSpinner && (
				<div className="lookupInformation">
					<SpinningCircle className="throbber" />
					<div>
						{formatMessage({
							id: "komid.register.phone.lookup",
						})}
					</div>
				</div>
			)}
		</>
	);
});
